import * as React from 'react';
import Styled, { css, ThemeContext } from 'styled-components';

import { FONT_MAP } from '../../../lib/StyledComponents/StyledComponents';
import SVGIcon from '../../Icon/SVGIcon';
import IThemeInterface from '../Theme/common';
import { VALID_GLOBAL_FONT_SIZES } from '../Typography/common';
import { convertNonPxStrToPxBasedNum, cssDeclaration, REM_NUM_CONVERSION, VALID_COLOR_DATA } from '../Utils';

import { BULLETS, ICONS, NUMBERS, VALID_LIST_STYLES } from './List';
import { useListContext } from './ListItem';

interface IStyledListCommon {
  listStyle: VALID_LIST_STYLES;
}

interface IListItemIcon {
  colorProp?: VALID_COLOR_DATA;
}

// calculates the offset of the list element marker use formula: (line height - size) / 2
const calcListStyleElOffset = (
  theme: IThemeInterface,
  lineheight: string,
  listStyleSize: string,
  fontSize: VALID_GLOBAL_FONT_SIZES,
): string => {
  const fontSizeVal = theme.global.font.size[fontSize];
  // NEW REPO ALERT convertNonPxStrToPxBasedNum change to convertStrWithUnitToPXBaseNum
  const lineHeightVal = +lineheight * convertNonPxStrToPxBasedNum(fontSizeVal);
  const listStyleSizeVal = convertNonPxStrToPxBasedNum(listStyleSize);

  return `${((lineHeightVal - listStyleSizeVal) / 2) / REM_NUM_CONVERSION}rem`;
};

export const StyledList = Styled(props => {
  const { listStyleProps } = useListContext();

  return (
    listStyleProps.type === NUMBERS ? <StyledList as='ol' {...props} /> : <StyledList as='ul' {...props} />
  );
})<IStyledListCommon>`
  ${({ theme: { themeTwo }, listStyle }) => {
    const { list: { listItem: { common, bullet, icon, number: numberType }}} = themeTwo;
    const bulletSpacing = themeTwo.global.edgeSize[bullet.listStyleSpacing] || bullet.listStyleSpacing;
    const bulletSize = themeTwo.global.edgeSize[bullet.size] || bullet.size;
    const iconSpacing = themeTwo.global.edgeSize[icon.listStyleSpacing] || icon.listStyleSpacing;
    const icontSize = themeTwo.global.edgeSize[icon.size] || icon.size;
    const isBulletOrIcon = listStyle === BULLETS || listStyle === ICONS;

    return css`
      ${listStyle === NUMBERS && css`
        margin-left: ${themeTwo.global.edgeSize[numberType.listStyleSpacing] || numberType.listStyleSpacing};
        counter-reset: decimal-bold;
      `}

      ${listStyle === BULLETS && css`
        & > ul {
          margin-left: calc(${bulletSpacing} + ${bulletSize});
        }
      `}

      ${listStyle === ICONS && css`
        & > ul {
          margin-left: calc(${iconSpacing} + ${icontSize});
        }
      `}

      ${isBulletOrIcon ? css`
        & > ul > li:last-of-type {
          /* margin */ ${cssDeclaration(themeTwo, 'margin', common.margin)};
        }
      ` : css`
          & > li > ol,
          & > li > ul,
          & > li + ul {
            /* because ol's markup differs from ul's; margin must be moved to the top of the ol when nested */
            margin-top: ${themeTwo.global.edgeSize[common.margin.bottom] || common.margin.bottom};
          }
      `}
    `;
  }}
`;

const commonListItemStyles = css`
  position: relative;

  ${({ theme: { themeTwo } }) => {
    const { list: { listItem: { common }}} = themeTwo;

    return css`
      font-family: ${common.font.family && FONT_MAP[common.font.family]};
      line-height: ${common.font.lineHeight};
      font-weight: ${common.font.weight};
      /* color */ ${cssDeclaration(themeTwo, 'color', common.font.color)};
      /* font-size */ ${cssDeclaration(themeTwo, 'font-size', common.font.size)};

      &:not(:last-of-type) {
        /* margin */ ${cssDeclaration(themeTwo, 'margin', common.margin)};
      }
    `;
  }}
`;

export const StyledListItem = Styled.li`
  ${commonListItemStyles}

  display: flex;
`;

export const ListItemNumber = Styled.li<IListItemIcon>`
  ${commonListItemStyles}
  display: block;

  ${({ theme: { themeTwo }, colorProp }) => {
    const { list: { listItem: { number: numberIcon }}} = themeTwo;
    const color = colorProp ? colorProp : numberIcon.font.color;

    return css`
      &::before {
        counter-increment: decimal-bold;
        content: counter(decimal-bold) '.';
        position: absolute;
        top: 0;
        font-weight: ${numberIcon.font.weight};
        left: -${themeTwo.global.edgeSize[numberIcon.listStyleSpacing] || numberIcon.listStyleSpacing};
        /* color */ ${cssDeclaration(themeTwo, 'color', color)};
      }
    `;
  }}


`;

export const ListItemBulletIcon = Styled.span<IListItemIcon>`
  border-radius: 50%;
  flex-shrink: 0;

  ${({ theme: { themeTwo }, colorProp }) => {
    const { list: { listItem: { common, bullet }}} = themeTwo;
    const bulletSize = themeTwo.global.edgeSize[bullet.size] || bullet.size;
    const backgroundColor = colorProp ? colorProp : bullet.backgroundColor;
    const bulletOffSet = calcListStyleElOffset(themeTwo, common.font.lineHeight, bulletSize, common.font.size);

    return css`
      height: ${bulletSize};
      width: ${bulletSize};
      margin-top: ${bulletOffSet};
      margin-right: ${themeTwo.global.edgeSize[bullet.listStyleSpacing] || bullet.listStyleSpacing};
      /* background-color */ ${cssDeclaration(themeTwo, 'background-color', backgroundColor)};
    `;
  }}
`;

export const ListItemWithIcon = Styled(({ icon, colorProp, size, ...rest }) => {
  const { themeTwo: { list: { listItem: { icon: iconTheme }}}} = React.useContext(ThemeContext);
  const color = colorProp ? colorProp : iconTheme.color;
  const iconType = icon ? icon : iconTheme.type;
  const iconSize = size ? size : iconTheme.size;

  return <SVGIcon {...rest} icon={iconType} size={iconSize} color={color} />;
})<IListItemIcon>`
  ${({ theme: { themeTwo }, size }) => {
    const { list: { listItem: { common, icon }}} = themeTwo;
    const iconSize = !size ?
      themeTwo.global.edgeSize[icon.size] || icon.size :
      themeTwo.global.edgeSize[size] || size; // custom size from component prop

    const iconOffSet = calcListStyleElOffset(themeTwo, common.font.lineHeight, iconSize, common.font.size);

    return css`
      margin-right: ${themeTwo.global.edgeSize[icon.listStyleSpacing] || icon.listStyleSpacing};

      svg {
        margin-top: ${iconOffSet};
      }
    `;
  }}
`;

export const ListItemTextWrapper = Styled.span`
  ${({ theme }) => {
    const isAmount = theme.name === 'amount' || theme.name === 'amountDark';

    return css`
      ${isAmount && css`
        transform: translateY(2.5px);
      `}
    `;
  }}
`;
